import { motion } from "framer-motion";
import { Gift } from "lucide-react";
import { useMyGoals } from "@/hooks/useGoals";
import { GOAL_TYPE_LABEL, formatGoalValue } from "@/lib/goal-format";

/**
 * "This week's goal" -- surfaces the child's most-imminent ACTIVE goal (set
 * by a linked parent). Non-negotiable per the parent portal plan: a goal the
 * child can't see is nagging, not motivation. Renders nothing when there's
 * no active goal so it never crowds students without linked parents.
 */
export function GoalWidget() {
  const goals = useMyGoals();
  const active = (goals.data ?? [])
    .filter((g) => g.status === "ACTIVE")
    .sort((a, b) => a.end_date.localeCompare(b.end_date))[0];
  if (!active) return null;

  const current = active.current_value ?? 0;
  const pct = Math.min(100, Math.round((current / active.target_value) * 100));

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-border bg-surface p-4 sm:p-5"
    >
      <p className="text-xs uppercase tracking-[0.1em] text-text-subtle">This week's goal</p>
      <p className="mt-1 font-medium text-sm">
        {GOAL_TYPE_LABEL[active.type]}: {formatGoalValue(active.type, current)} of{" "}
        {formatGoalValue(active.type, active.target_value)}
      </p>
      <div className="mt-2.5 h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-brand"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ type: "spring", stiffness: 200, damping: 28 }}
        />
      </div>
      {active.reward_text ? (
        <p className="mt-2.5 flex items-center gap-1.5 text-xs text-brand">
          <Gift className="h-3.5 w-3.5 flex-shrink-0" />
          {active.reward_text}
        </p>
      ) : null}
    </motion.div>
  );
}
